<template>
    <div>
        <h2>Profile</h2>
        <Spinner v-if="displaySpinner" />
        <Tabs value="general">
            <TabList>
                <Tab value="general">General</Tab>
                <Tab value="roles">Organizations & Roles</Tab>
                <Tab value="security">Security</Tab>
                <Tab value="api-keys">API Keys</Tab>
            </TabList>
            <TabPanels>
                <TabPanel value="general">
                    <div class="general-section">
                        <p>General information about your profile.</p>
                        <div class="form-field">
                            <label for="fullname">Name</label>
                            <InputText
                                id="fullname"
                                type="text"
                                v-model="genForm.fullname"
                                disabled
                            />
                        </div>
                        <div class="form-field">
                            <label for="email">Email</label>
                            <InputText
                                id="email"
                                type="text"
                                v-model="genForm.email"
                                disabled
                            />
                        </div>
                        <div class="button-group">
                            <Button
                                label="Cancel"
                                severity="secondary"
                                disabled
                            />
                            <Button disabled label="Update Changes" />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel value="roles">
                    <div class="general-section">
                        <div class="roles-header">
                            <h3>Organizations &amp; Roles</h3>
                            <Button
                                label="Select Organization"
                                icon="pi pi-sync"
                                @click="
                                    router.push({ name: 'organizationPage' })
                                "
                                v-if="organizations && organizations.length > 1"
                                severity="secondary"
                            />
                        </div>
                        <p>
                            A list of all organizations you have access to and
                            which role you have for them.
                        </p>
                        <div
                            v-if="
                                !user?.roles ||
                                Object.keys(user.roles).length === 0
                            "
                            class="empty-roles"
                        >
                            You don't have any roles assigned yet.
                        </div>
                        <DataTable
                            v-else
                            :value="formattedRoles"
                            class="roles-table"
                            stripedRows
                        >
                            <Column
                                field="organization"
                                header="Organization"
                                sortable
                            />
                            <Column field="role" header="Role" sortable>
                                <template #body="slotProps">
                                    <span
                                        :class="
                                            'role-badge ' + slotProps.data.role
                                        "
                                        >{{ slotProps.data.role }}</span
                                    >
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </TabPanel>

                <TabPanel value="security">
                    <Button
                        class="p-button-link"
                        label="Sign out"
                        @click="signOutUser"
                    />
                    <br />
                    <Button
                        class="p-button-link"
                        label="Change password"
                        @click="initiatePasswordReset"
                    />
                    <br />
                    <Button
                        class="p-button-link"
                        label="Reset MFA"
                        :disabled="!authUser.mfaEnabled"
                        @click="() => resetMfa()"
                    />
                    <br />
                    <Button
                        class="p-button-link"
                        label="Control account access"
                        :disabled="true"
                    />
                    <br />
                    <Button
                        class="p-button-link"
                        label="Download personal data"
                        :disabled="true"
                    />
                    <br />
                    <div class="delete-account" style="float: right">
                        <Button
                            class="p-button-danger"
                            label="Delete Account"
                            :disabled="true"
                        />
                    </div>
                </TabPanel>

                <TabPanel value="api-keys">
                    <div class="api-keys-section">
                        <h3>Your API Keys</h3>
                        <p>
                            API keys allow external applications to access the
                            API on your behalf.
                        </p>

                        <div class="api-key-actions">
                            <Button
                                label="Create New API Key"
                                icon="pi pi-plus"
                                @click="apiKeyStore.openCreateApiKeyDialog()"
                                class="p-button-primary mr-2"
                            />
                            <Button
                                label="Create New API Key for MCP"
                                icon="pi pi-plus"
                                @click="apiKeyStore.openCreateMcpApiKeyDialog()"
                                class="p-button-secondary"
                            />
                        </div>

                        <div
                            v-if="isLoading && apiKeys.length === 0"
                            class="loading-container"
                        >
                            <i
                                class="pi pi-spin pi-spinner"
                                style="font-size: 2rem"
                            ></i>
                            <p>Loading your API keys...</p>
                        </div>

                        <div v-else-if="error" class="error-container">
                            <i
                                class="pi pi-exclamation-triangle"
                                style="font-size: 1.5rem; color: #f44336"
                            ></i>
                            <p>{{ error }}</p>
                            <Button
                                label="Try Again"
                                @click="retryLoading"
                                class="p-button-sm mt-2"
                            />
                        </div>

                        <DataTable
                            v-else
                            :value="apiKeys"
                            :loading="isLoading"
                            class="api-keys-table"
                            :rows="10"
                            paginator
                            :emptyMessage="'No API keys found. Create one above.'"
                        >
                            <template #empty>
                                <p>
                                    No API keys found. Create your first API key
                                    using the form above.
                                </p>
                            </template>
                            <Column field="name" header="Name" sortable />
                            <Column
                                field="created_at"
                                header="Created"
                                sortable
                            >
                                <template #body="slotProps">
                                    {{ formatDate(slotProps.data.created_at) }}
                                </template>
                            </Column>
                            <Column
                                field="expires_at"
                                header="Expires"
                                sortable
                            >
                                <template #body="slotProps">
                                    {{
                                        slotProps.data.expires_at
                                            ? formatDate(
                                                  slotProps.data.expires_at
                                              )
                                            : "Never"
                                    }}
                                </template>
                            </Column>
                            <Column
                                field="last_used_at"
                                header="Last Used"
                                sortable
                            >
                                <template #body="slotProps">
                                    {{
                                        slotProps.data.last_used_at
                                            ? formatDate(
                                                  slotProps.data.last_used_at
                                              )
                                            : "Never"
                                    }}
                                </template>
                            </Column>
                            <Column header="Actions" style="width: 120px">
                                <template #body="slotProps">
                                    <Button
                                        severity="danger"
                                        text
                                        @click="
                                            (e) =>
                                                revokeApiKey(e, slotProps.data)
                                        "
                                        label="Revoke"
                                    />
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </TabPanel>
            </TabPanels>
        </Tabs>
    </div>
</template>

<script setup lang="ts">
import Spinner from "@/shared/Spinner.vue";
import {
    useApiKeyManager,
    useApiKeyStore,
    type ApiKey,
} from "@/stores/api-key-store";
import {
    useAuthenticationManager,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import { useOrganizationsManager } from "@/stores/organizations-store";
import { useUserManager, useUserStore } from "@/stores/user-store";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

const toast = useToast();
const router = useRouter();
const apiKeyStore = useApiKeyStore();
const { apiKeys, isLoading, error } = useApiKeyManager();
const { signOut, resetOtp, sendPasswordResetEmail } = useAuthenticationStore();
const { authUser } = useAuthenticationManager();
const { user } = useUserManager();
const { reset: resetUser } = useUserStore();
const { organizations } = useOrganizationsManager();
const displaySpinner = ref(false);
const genForm = ref({
    fullname: "",
    email: "",
});

// Format user roles for the DataTable
const formattedRoles = computed(() => {
    if (!user.value?.roles) return [];

    return Object.entries(user.value.roles).map(([orgId, role]) => {
        // Find organization name if available
        const org = organizations.value?.find((o) => o.identity === orgId);

        return {
            organization: org?.name || orgId,
            role: role,
        };
    });
});

// Retry loading API keys
const retryLoading = async () => {
    await apiKeyStore.refresh();
};

const confirm = useConfirm();

const revokeApiKey = async (event: Event, key: ApiKey) => {
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: "Are you sure? This action cannot be undone.",
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Revoke",
            severity: "danger",
        },
        accept: async () => {
            const success = await apiKeyStore.revokeApiKey(key.identity);
            if (success) {
                toast.add({
                    severity: "success",
                    summary: "API key revoked",
                    detail: `The API key "${key.name}" has been revoked successfully.`,
                    life: 3000,
                });
            }
        },
    });
};

const formatDate = (dateStr?: Date | string | null): string => {
    if (!dateStr) return "Never";
    if (typeof dateStr === "string") {
        return new Date(dateStr).toLocaleString();
    } else {
        return dateStr.toLocaleString();
    }
};

const signOutUser = async () => {
    await signOut({
        severity: "success",
        summary: "Succesfully logged out.",
        life: 5000,
    });
    router.push({ name: "home" });
    resetUser();
};

const resetMfa = async () => {
    try {
        await resetOtp();
        toast.add({
            severity: "success",
            summary:
                "Succesfully set up multi-factor authentication with Authenticator.",
        });
    } catch {
        toast.add({
            severity: "error",
            summary:
                "Failed to register an authenticator app. You cannot login until it's properly setup.",
        });
    }
};

const initiatePasswordReset = async () => {
    try {
        if (user.value?.email) {
            const success = await sendPasswordResetEmail(user.value.email);
            if (success) {
                toast.add({
                    severity: "success",
                    summary: "Password reset email sent",
                    detail: "Please check your email for instructions.",
                });
            }
        } else {
            toast.add({
                severity: "error",
                summary: "Error",
                detail: "Unable to find user email for password reset.",
            });
        }
    } catch (error) {
        toast.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to send password reset email.",
        });

        throw error;
    }
};

watch(
    user,
    (newUser) => {
        if (newUser) {
            genForm.value.fullname = newUser.name;
            genForm.value.email = newUser.email;
        }
        displaySpinner.value = false;
    },
    { immediate: true }
);
</script>

<style lang="scss" scoped>
.password-reset,
.general-section,
.public-key {
    display: inline-block;
    width: 100%;
    .action-btn {
        text-align: right;
    }
}
.subtitle {
    font-style: italic;
}

.loading-container,
.error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    background-color: #f8f9fa;
    border-radius: 8px;
}

.error-container {
    border-left: 4px solid #f44336;
}

.general-section .form-field {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 4px;
}
.general-section .button-group {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
}

.api-keys-section {
    padding: 1rem 0;
}

.api-keys-section h3 {
    margin-top: 0;
    margin-bottom: 1rem;
}

.api-key-actions {
    margin: 1.5rem 0;
    display: flex;
    gap: 10px;
}

.mr-2 {
    margin-right: 0.5rem;
}

.roles-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.roles-header h3 {
    margin: 0;
}

.roles-table {
    margin-top: 1rem;
    width: 100%;
}

.empty-roles {
    margin-top: 1rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 4px;
    color: #6c757d;
    font-style: italic;
}

.role-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: capitalize;
}

.role-badge.user {
    background-color: #e3f2fd;
    color: #0277bd;
}

.role-badge.user-manager {
    background-color: #e8f5e9;
    color: #2e7d32;
}
</style>
